import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FiSend, FiUsers, FiBell, FiRefreshCw } from 'react-icons/fi';
import { fetchUsers } from '../../store/slices/usersSlice';
import notificationService from '../../services/notificationService';
import { DataTable, Pagination } from '../../components/Common';
import { formatDate } from '../../utils/helpers';

const Notifications = () => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.users);

  const [activeTab, setActiveTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Send notification form
  const [sendForm, setSendForm] = useState({
    userId: '',
    title: '',
    message: '',
    type: 'system',
  });

  // Broadcast form
  const [broadcastForm, setBroadcastForm] = useState({
    title: '',
    message: '',
    type: 'system',
  });

  const notificationTypes = [
    { value: 'system', label: 'System' },
    { value: 'promotion', label: 'Promotion' },
    { value: 'subscription', label: 'Subscription' },
    { value: 'message', label: 'Message' },
  ];

  useEffect(() => {
    dispatch(fetchUsers({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === 'all') {
      loadNotifications();
    }
  }, [activeTab, currentPage]);

  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await notificationService.getAllNotifications({
        page: currentPage,
        limit: 10,
      });
      // API returns { success, data, pagination }
      const resData = response.data;
      setNotifications(resData.data || []);
      setTotalNotifications(resData.pagination?.totalItems || 0);
      setTotalPages(resData.pagination?.totalPages || 1);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!sendForm.userId || !sendForm.title || !sendForm.message) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsSending(true);
    try {
      await notificationService.sendNotification(sendForm);
      toast.success('Notification sent successfully');
      setSendForm({ userId: '', title: '', message: '', type: 'system' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send notification');
    } finally {
      setIsSending(false);
    }
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastForm.title || !broadcastForm.message) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsSending(true);
    try {
      await notificationService.broadcastNotification(broadcastForm);
      toast.success('Notification broadcasted to all users');
      setBroadcastForm({ title: '', message: '', type: 'system' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to broadcast notification');
    } finally {
      setIsSending(false);
    }
  };

  const columns = [
    {
      header: 'User',
      render: (row) => row.userId?.fullName || row.userId?.mobile || 'N/A',
    },
    { header: 'Title', render: (row) => row.title },
    {
      header: 'Message',
      render: (row) => (
        <span title={row.message}>
          {row.message?.length > 50 ? row.message.substring(0, 50) + '...' : row.message}
        </span>
      ),
    },
    {
      header: 'Type',
      render: (row) => (
        <span className={`badge badge-${row.type === 'system' ? 'primary' : 'secondary'}`}>
          {row.type}
        </span>
      ),
    },
    {
      header: 'Read',
      render: (row) => (
        <span className={`badge ${row.isRead ? 'badge-success' : 'badge-warning'}`}>
          {row.isRead ? 'Yes' : 'No'}
        </span>
      ),
    },
    { header: 'Date', render: (row) => formatDate(row.createdAt) },
  ];

  return (
    <div>
      {/* Stats Cards */}
      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div className="card">
          <div className="card-body">
            <h4>{totalNotifications}</h4>
            <p>Total Notifications</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h4>{notifications.filter(n => !n.isRead).length}</h4>
            <p>Unread (Current Page)</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h4>{users.length}</h4>
            <p>Total Users</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: '16px' }}>
        <button
          className={`btn ${activeTab === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('all')}
        >
          <FiBell style={{ marginRight: '4px' }} /> All Notifications
        </button>
        <button
          className={`btn ${activeTab === 'send' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('send')}
          style={{ marginLeft: '8px' }}
        >
          <FiSend style={{ marginRight: '4px' }} /> Send Notification
        </button>
        <button
          className={`btn ${activeTab === 'broadcast' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('broadcast')}
          style={{ marginLeft: '8px' }}
        >
          <FiUsers style={{ marginRight: '4px' }} /> Broadcast
        </button>
      </div>

      {/* All Notifications Tab */}
      {activeTab === 'all' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">All Notifications ({totalNotifications})</h3>
            <button className="btn btn-secondary" onClick={loadNotifications} disabled={isLoading}>
              <FiRefreshCw className={isLoading ? 'spin' : ''} /> Refresh
            </button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <DataTable columns={columns} data={notifications} isLoading={isLoading} />
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        </div>
      )}

      {/* Send Notification Tab */}
      {activeTab === 'send' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Send Notification to User</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSendNotification}>
              <div className="form-group">
                <label>Select User *</label>
                <select
                  className="form-control"
                  value={sendForm.userId}
                  onChange={(e) => setSendForm({ ...sendForm, userId: e.target.value })}
                  required
                >
                  <option value="">Select a user</option>
                  {users.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.fullName || user.mobile} - {user.mobile}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Notification Type</label>
                <select
                  className="form-control"
                  value={sendForm.type}
                  onChange={(e) => setSendForm({ ...sendForm, type: e.target.value })}
                >
                  {notificationTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  className="form-control"
                  value={sendForm.title}
                  onChange={(e) => setSendForm({ ...sendForm, title: e.target.value })}
                  placeholder="Notification title"
                  required
                />
              </div>
              <div className="form-group">
                <label>Message *</label>
                <textarea
                  className="form-control"
                  value={sendForm.message}
                  onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                  rows={4}
                  placeholder="Notification message"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={isSending}>
                {isSending ? 'Sending...' : 'Send Notification'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Broadcast Tab */}
      {activeTab === 'broadcast' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Broadcast to All Users</h3>
          </div>
          <div className="card-body">
            <div className="alert alert-info" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#e0f2fe', borderRadius: '8px' }}>
              This will send a notification to all active users in the system.
            </div>
            <form onSubmit={handleBroadcast}>
              <div className="form-group">
                <label>Notification Type</label>
                <select
                  className="form-control"
                  value={broadcastForm.type}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, type: e.target.value })}
                >
                  {notificationTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  className="form-control"
                  value={broadcastForm.title}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                  placeholder="Notification title"
                  required
                />
              </div>
              <div className="form-group">
                <label>Message *</label>
                <textarea
                  className="form-control"
                  value={broadcastForm.message}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, message: e.target.value })}
                  rows={4}
                  placeholder="Notification message"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={isSending}>
                {isSending ? 'Broadcasting...' : 'Broadcast to All Users'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
